import { Film, Clock, SlidersHorizontal } from 'lucide-react';
import { useAppStore } from '@/stores/useAppStore';
import { TRANSITION_SOUNDS, FPS_OPTIONS, LOUDNESS_PRESETS, ADDITIONAL_TIME } from '@/lib/constants';

export function VideoSettingsContent() {
  const { videoSettings, updateVideoSettings } = useAppStore();

  return (
    <div className="space-y-6">
      {/* Video Settings */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
            <Film size={16} className="text-teal-600" />
          </div>
          <h3 className="font-display font-semibold text-warm-800">動画設定</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-x-4 gap-y-4 md:items-center pl-10">
          <span className="text-sm font-medium text-warm-700">FPS</span>
          <div className="flex justify-between w-full">
            {FPS_OPTIONS.map((fps) => (
              <button
                key={fps}
                onClick={() => updateVideoSettings({ fps })}
                className={`fps-button ${videoSettings.fps === fps ? 'fps-button-active' : ''}`}
              >
                {fps}
              </button>
            ))}
          </div>

          <span className="text-sm text-warm-700">トランジションエフェクト</span>
          <button
            type="button"
            role="switch"
            aria-checked={videoSettings.enableTransition}
            onClick={() => updateVideoSettings({ enableTransition: !videoSettings.enableTransition })}
            className={`toggle-switch justify-self-start md:justify-self-end ${
              videoSettings.enableTransition ? 'toggle-switch-on' : 'toggle-switch-off'
            }`}
          >
            <span className={`toggle-knob ${videoSettings.enableTransition ? 'toggle-knob-on' : ''}`} />
          </button>

          <span className={`text-sm font-medium text-warm-700 ${!videoSettings.enableTransition ? 'opacity-50' : ''}`}>切り替え効果音</span>
          <select
            value={videoSettings.transitionSound}
            onChange={(e) => updateVideoSettings({ transitionSound: e.target.value })}
            className={`select-base w-full md:w-auto ${!videoSettings.enableTransition ? 'opacity-50 pointer-events-none' : ''}`}
            disabled={!videoSettings.enableTransition}
          >
            {TRANSITION_SOUNDS.map((sound) => (
              <option key={sound.id} value={sound.id}>
                {sound.name}
              </option>
            ))}
          </select>

          <span className={`text-sm font-medium text-warm-700 ${!videoSettings.enableTransition ? 'opacity-50' : ''}`}>トランジション色</span>
          <div className={`flex items-center gap-2 ${!videoSettings.enableTransition ? 'opacity-50 pointer-events-none' : ''}`}>
            <input
              type="color"
              value={videoSettings.transitionColor}
              onChange={(e) => updateVideoSettings({ transitionColor: e.target.value })}
              className="color-input"
              disabled={!videoSettings.enableTransition}
            />
            <input
              type="text"
              value={videoSettings.transitionColor}
              onChange={(e) => updateVideoSettings({ transitionColor: e.target.value })}
              className="input-base w-24 font-mono text-sm"
              placeholder="#000000"
              disabled={!videoSettings.enableTransition}
            />
          </div>
        </div>
      </section>

      <div className="divider divider-coral" />

      {/* Display Time Settings */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-gold-100 flex items-center justify-center">
            <Clock size={16} className="text-amber-600" />
          </div>
          <h3 className="font-display font-semibold text-warm-800">表示時間設定</h3>
        </div>
        <div className="space-y-4 pl-10">
          <div>
            <label className="block text-sm font-medium text-warm-700 mb-1">
              デフォルトの追加表示時間
            </label>
            <p className="text-xs text-warm-400 mb-2">音声の長さに追加される余韻の時間</p>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={ADDITIONAL_TIME.MIN}
                max={ADDITIONAL_TIME.MAX}
                step={ADDITIONAL_TIME.STEP}
                value={videoSettings.defaultAdditionalTime ?? 0.5}
                onChange={(e) => updateVideoSettings({ defaultAdditionalTime: parseFloat(e.target.value) })}
                className="flex-1 h-1.5 bg-warm-200 rounded-full appearance-none cursor-pointer accent-coral-500"
              />
              <input
                type="number"
                min={ADDITIONAL_TIME.MIN}
                max={ADDITIONAL_TIME.MAX}
                step={ADDITIONAL_TIME.STEP}
                value={videoSettings.defaultAdditionalTime ?? 0.5}
                onChange={(e) => {
                  const parsed = parseFloat(e.target.value);
                  // 入力途中の空欄などを0として確定させない
                  if (Number.isNaN(parsed)) return;
                  const value = Math.min(ADDITIONAL_TIME.MAX, Math.max(ADDITIONAL_TIME.MIN, parsed));
                  updateVideoSettings({ defaultAdditionalTime: value });
                }}
                className="w-20 px-2 py-1 text-sm font-medium text-warm-600 text-right border border-warm-200 rounded-md bg-white focus:outline-none focus:border-coral-300"
              />
              <span className="text-sm text-warm-500">秒</span>
            </div>
          </div>
        </div>
      </section>

      <div className="divider divider-coral" />

      {/* Audio Normalization Settings */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
            <SlidersHorizontal size={16} className="text-purple-600" />
          </div>
          <h3 className="font-display font-semibold text-warm-800">音量設定</h3>
        </div>
        <div className="space-y-4 pl-10">
          <div className="flex items-center justify-between py-2">
            <div>
              <span className="text-sm text-warm-700">音量を均等化する</span>
              <p className="text-xs text-warm-400 mt-0.5">シーン間の音量差を解消</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={videoSettings.enableAudioNormalization}
              onClick={() => updateVideoSettings({ enableAudioNormalization: !videoSettings.enableAudioNormalization })}
              className={`toggle-switch ${
                videoSettings.enableAudioNormalization ? 'toggle-switch-on' : 'toggle-switch-off'
              }`}
            >
              <span className={`toggle-knob ${videoSettings.enableAudioNormalization ? 'toggle-knob-on' : ''}`} />
            </button>
          </div>

          <div className={!videoSettings.enableAudioNormalization ? 'opacity-50 pointer-events-none' : ''}>
            <label className="block text-sm font-medium text-warm-700 mb-1">
              目標ラウドネス
              <span className="ml-2 text-coral-600">{videoSettings.targetLoudness} LUFS</span>
            </label>
            <input
              type="range"
              min="-23"
              max="-14"
              step="1"
              value={videoSettings.targetLoudness}
              onChange={(e) => updateVideoSettings({ targetLoudness: parseFloat(e.target.value) })}
              className="w-full h-1.5 bg-warm-200 rounded-full appearance-none cursor-pointer accent-coral-500"
              disabled={!videoSettings.enableAudioNormalization}
            />
            <div className="flex justify-between text-xs text-warm-400 mt-1">
              <span>静か</span>
              <span>標準</span>
              <span>大きめ</span>
            </div>
            <div className="flex justify-center gap-2 mt-3">
              {LOUDNESS_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => updateVideoSettings({ targetLoudness: preset.value })}
                  className={`fps-button ${videoSettings.targetLoudness === preset.value ? 'fps-button-active' : ''}`}
                  disabled={!videoSettings.enableAudioNormalization}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
